import React, {useEffect, useState} from 'react';
import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useNavigate} from 'shared/reload/helper/useNavigate';
import {useConfirmationDialog} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {genericMessageHandler} from 'shared/helpers';
import {PartialJob} from 'shared/types/PartialJob';
import {SidebarProduct} from 'shared/components/Header/entity/SidebarProduct';
import {
    invalidateProductAndCost,
    useDeleteCabinetMutation,
} from 'components/customer/Product/store/productApi';
import {useAppDispatch} from 'store/customer';
import {invalidateTotalProductCount} from 'components/customer/Job/store/jobApi';
import {matchPath, useLocation, useSearchParams} from 'react-router-dom';
import {setProductDataLoading} from 'components/customer/Product/store/productSlice';
import {
    Event,
    genericPubSub,
} from 'components/customer/RoomPlanner/helper/GenericPubSub';
import {useRoomPlannerLeftMenu} from 'components/customer/RoomPlanner/helper/useRoomPlannerLeftMenu';
import {setSelectedMenu} from 'components/customer/RoomPlanner/helper/useRoomPlannerPreview';
import {MenuItem} from 'components/customer/RoomPlanner/components/Menu/MenuContext';
import {dispatchClearChanges} from 'store/customer/appSlice';
import {setIsRoomPlannerEditing} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {buildCabinetEditorParams} from 'components/customer/RoomPlanner/helpers/placeholder/editorParams';

export const useProductActions = (roomId?: number) => {
    const {notify} = useNotificationContext();
    const {job, room, updateTotalProductCountManual} =
        useJobContext() as PartialJob;
    const {showDialog, dialog} = useConfirmationDialog();
    const navigate = useNavigate();
    const [deleteProduct] = useDeleteCabinetMutation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const {isRoomPlannerPage} = useRoomPlannerLeftMenu();
    const [searchParams, setSearchParams] = useSearchParams();
    const [cabinetId, setCabinetId] = useState<number>();
    const {
        cabinetGroupListRef,
        setSelectedCabinetGroup,
        setSelectedProduct,
        hasUnsavedProductChanges,
        confirmDiscardProductChanges,
    } = useRoomPlannerContext();
    const product = searchParams.get('product');
    const preventReset = searchParams.get('preventReset');
    const productRoomId = roomId ?? room?.id;

    const closeRoomPlannerEditingSidebar = () => {
        searchParams.delete('cabinetId');
        if (product) searchParams.delete('product');
        if (preventReset) searchParams.delete('preventReset');

        setSearchParams(searchParams);

        dispatch(setIsRoomPlannerEditing(false));
        setSelectedCabinetGroup && setSelectedCabinetGroup(null);
    };

    const removeCabinet = (
        jobCabinet: SidebarProduct,
        event: React.MouseEvent<HTMLElement>
    ) => {
        const button = event.currentTarget.parentElement as HTMLButtonElement;
        showDialog({
            title: 'Delete item',
            message: 'Are you sure you want to delete this item from the job?',
            yes: async () => {
                try {
                    button.disabled = true;
                    await deleteProduct({cabinetId: jobCabinet.id});
                    dispatch(invalidateProductAndCost());
                    dispatch(invalidateTotalProductCount());
                    dispatchClearChanges();

                    button.disabled = false;
                    genericMessageHandler(
                        notify,
                        {message: 'Item removed.'},
                        'success'
                    );

                    if (cabinetId && cabinetId == jobCabinet.id) {
                        if (isRoomPlannerPage) {
                            void genericPubSub.publish(
                                Event.ROOM_PLANNER_CART_DELETE,
                                jobCabinet.id
                            );
                            closeRoomPlannerEditingSidebar();
                        } else {
                            navigate(`/v2/job/${job.displayId}/dashboard`);
                        }
                    } else {
                        if (isRoomPlannerPage) {
                            void genericPubSub.publish(
                                Event.ROOM_PLANNER_CART_DELETE,
                                jobCabinet.id
                            );
                        }
                    }
                } catch (e) {
                    button.disabled = false;
                    genericMessageHandler(notify, e);
                }
            },
        });
    };

    const editCabinet = (
        row: SidebarProduct,
        copy = false,
        isQFP = false,
        event: React.MouseEvent<HTMLElement>
    ) => {
        event.stopPropagation();

        const is3dRoomPlanner = !!matchPath(
            '/v2/job/:jobId/room/:roomId/room-planner',
            location.pathname
        );

        if (is3dRoomPlanner) {
            const switchToProduct = () => {
                dispatch(setIsRoomPlannerEditing(true));
                navigate({
                    pathname: '.',
                    search: buildCabinetEditorParams(
                        String(row.id),
                        String(row.type)
                    ).toString(),
                });

                setSelectedProduct && setSelectedProduct(row.id);
                setSelectedMenu(MenuItem.ADD_PRODUCT);
            };

            if (hasUnsavedProductChanges) {
                confirmDiscardProductChanges?.(switchToProduct);
            } else {
                switchToProduct();
            }
            return;
        }

        let url = `/v2/job/${job.displayId}/room/${productRoomId}/product?product=${row.type}&cabinetId=${row.id}`;
        if (copy) {
            url += '&copy';
        }
        if (isQFP) {
            url = `/v2/job/${job.displayId}/room/${productRoomId}/quick-flat-product?edit=${row.id}`;
        }

        if (!isQFP && cabinetId != row.id) {
            dispatch(setProductDataLoading(true));
        }
        navigate(url);

        void genericPubSub.publish(
            Event.ROOM_PLANNER_CLEAR_PRODUCT_EDITOR,
            undefined
        );
    };

    const copyCabinet = (
        row: SidebarProduct,
        event: React.MouseEvent<HTMLElement>
    ) => {
        if (isRoomPlannerPage) {
            const selected = cabinetGroupListRef?.current.find(
                (mesh) => mesh.userData.values.id == row.id
            );
            const duplicate = (
                selected?.userData as {
                    duplicateCabinet?: () => void;
                }
            )?.duplicateCabinet;

            if (selected && typeof duplicate === 'function') {
                setSelectedCabinetGroup && setSelectedCabinetGroup(selected);
                duplicate();
                updateTotalProductCountManual(false, true, 1);
            } else {
                const url = `/v2/job/${job.displayId}/room/${productRoomId}/room-planner?product=${row.type}`;
                dispatch(setProductDataLoading(true));
                navigate(url);
            }
        } else {
            editCabinet(row, true, false, event);
        }
    };

    const editQFP = (row: SidebarProduct) => {
        navigate(
            `/v2/job/${job.displayId}/room/${productRoomId}/quick-flat-product?edit=${row.id}`
        );
    };

    useEffect(() => {
        if (searchParams) {
            const paramCabinetId =
                (searchParams.get('cabinetId') ||
                    searchParams.get('selected')) ??
                '';

            const cabinetIdLocal = parseInt(paramCabinetId);

            if (cabinetIdLocal) {
                if (cabinetIdLocal != cabinetId) {
                    setCabinetId(cabinetIdLocal);
                }
            } else {
                setCabinetId(undefined);
            }
        }
    }, [searchParams]);

    return {
        dialog,
        cabinetId,
        hasUnsavedProductChanges,
        confirmDiscardProductChanges,
        removeCabinet,
        editCabinet,
        copyCabinet,
        editQFP,
    };
};
